import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/errors/use-toast';
import { extractCleanQuestionText } from '@/hooks/research-generator/related-questions/utils';

export class ResearchLibraryClient {
  static supabase = supabase;

  /**
   * Clean related questions by extracting only question text and removing research_id references
   * Handles mixed formats: strings, objects with research_id, and JSON strings
   */
  private static cleanRelatedQuestions(questions: any[]): string[] {
    if (!Array.isArray(questions)) return [];
    
    return questions.map(question => {
      if (typeof question === 'string') {
        // Handle both plain strings and JSON strings
        return extractCleanQuestionText(question);
      } else if (typeof question === 'object' && question !== null && 'question' in question) {
        // Extract just the question text, strip the research_id
        return question.question;
      }
      return String(question); // Fallback for any other format
    });
  }

  /**
   * Copy research from research_library to workbook_research for project assignment
   * Uses junction table approach following private research pattern
   */
  static async copyToWorkbookResearch(
    researchLibraryId: string, 
    userId: string, 
    projectId: string
  ): Promise<void> {
    // 1. Fetch research data from research_library
    const { data: researchData, error: fetchError } = await supabase
      .from('research_library')
      .select('*')
      .eq('id', researchLibraryId)
      .single();

    if (fetchError) {
      throw new Error(`Failed to fetch research from library: ${fetchError.message}`);
    }

    if (!researchData) {
      throw new Error('Research not found in library');
    }

    // 2. Find the goal for this project (required for junction table)
    const { data: goalData, error: goalError } = await supabase
      .from('planner_goals_workbooks')
      .select('goal_id')
      .eq('workbook_id', projectId)
      .eq('user_id', userId) // Security check
      .single();

    if (goalError) {
      if (goalError.code === 'PGRST116') {
        throw new Error('Project has no associated goal. Cannot add research.');
      }
      throw goalError;
    }

    // 3. Clean related questions to remove research_id references
    const cleanedQuestions = this.cleanRelatedQuestions(
      Array.isArray(researchData.related_questions) 
        ? researchData.related_questions 
        : []
    );

    console.log('Research copy - Original related questions:', researchData.related_questions);
    console.log('Research copy - Cleaned related questions:', cleanedQuestions);

    // 4. Insert into workbook_research with workbook_id = null (junction table approach)
    const { data: insertedResearch, error: insertError } = await supabase
      .from('workbook_research')
      .insert({
        user_id: userId,
        workbook_id: null, // ✅ CORRECTED: Use junction table, not direct assignment
        page_id: null, // Not assigned to any specific page
        title: researchData.title,
        content: researchData.content,
        research_type: researchData.research_type,
        original_prompt: researchData.original_prompt,
        metadata: researchData.metadata,
        sources: researchData.sources,
        related_questions: cleanedQuestions,
        usage: researchData.usage,
        research_parameters: researchData.research_parameters,
        system_prompt: researchData.system_prompt,
        generated_research_count: researchData.generated_research_count || 0
      })
      .select()
      .single();

    if (insertError) {
      throw new Error(`Failed to copy research to workbook: ${insertError.message}`);
    }

    // 5. Create junction table entry linking research to project goal
    const { error: junctionError } = await supabase
      .from('planner_suggested_research')
      .insert({
        goal_id: goalData.goal_id,
        user_id: userId,
        title: `Copied Research: ${researchData.title}`,
        description: `Research copied from public library`,
        status: 'pending',
        generated_research: insertedResearch.id,
        display_order: 999 // Put at end of list
      });

    if (junctionError) {
      // Cleanup: Remove the research we just created since junction failed
      await supabase
        .from('workbook_research')
        .delete()
        .eq('id', insertedResearch.id);
      
      throw new Error(`Failed to link research to project: ${junctionError.message}`);
    }

    console.log('Research copied successfully from library to project via junction table:', {
      originalId: researchLibraryId,
      copiedId: insertedResearch.id,
      projectId,
      goalId: goalData.goal_id,
      originalQuestionsCount: Array.isArray(researchData.related_questions) ? researchData.related_questions.length : 0,
      cleanedQuestionsCount: cleanedQuestions.length
    });
  }

  /**
   * Check if a public research has been copied to a specific project
   * Uses junction table approach following private research pattern
   */
  static async checkResearchInProject(
    researchLibraryId: string, 
    userId: string, 
    projectId: string
  ): Promise<boolean> {
    try {
      // 1. Get the goal for this project
      const { data: goalData, error: goalError } = await supabase
        .from('planner_goals_workbooks')
        .select('goal_id')
        .eq('workbook_id', projectId)
        .eq('user_id', userId)
        .single();

      if (goalError || !goalData) {
        return false; // No goal means no research can be assigned
      }

      // 2. Get original research data for comparison
      const { data: researchData, error: fetchError } = await supabase
        .from('research_library')
        .select('title, original_prompt')
        .eq('id', researchLibraryId)
        .single();

      if (fetchError || !researchData) {
        return false;
      }

      // 3. Check if there's a junction entry for research copied from this source
      const { data: junctionEntries, error: junctionError } = await supabase
        .from('planner_suggested_research')
        .select(`
          generated_research,
          workbook_research!inner (
            title,
            original_prompt
          )
        `)
        .eq('goal_id', goalData.goal_id)
        .eq('user_id', userId)
        .not('generated_research', 'is', null);

      if (junctionError || !junctionEntries) {
        return false;
      }

      // 4. Check if any of the linked research matches our original research
      return junctionEntries.some(entry => 
        entry.workbook_research?.title === researchData.title &&
        entry.workbook_research?.original_prompt === researchData.original_prompt
      );
    } catch (error) {
      console.warn('Error checking research in project:', error);
      return false;
    }
  }
} 